import React from "react";
// Grommet
import { ResponsiveContext } from "grommet";
import TermsService, { recordTerms } from "../../services/TermsService";

// Appt Components
// Appt Components
import Anchor from "../../components/simple/anchor/Anchor";
import Box from "../../components/simple/box/Box";
import CheckBox from "../../components/simple/input/CheckBox";
import DataTable from "../../components/simple/dataTable/DataTable.js";
import Divider from "../../components/simple/divider/Divider";
import FontAwesome from "../../components/compound/fontAwesome/FontAwesome";
import FormField from "../../components/simple/formField/FormField.js";
import Grid from "../../components/simple/grid/Grid";
import Text from "../../components/simple/text/Text";
import TextInput from "../../components/simple/input/TextInput";
import SelectDynamic from "react-select";
import constants from "../../utils/constants";
import ActionButton from "../../components/compound/actionButton/ActionButton.js";
import Button from "../../components/simple/button/Button.js";
import ResponsiveGrid from "../../components/compound/responsiveGrid/ResponsiveGrid.js";
import H3 from "../../components/simple/heading/H3.js";

function TechniqueOptions ({ techniques, onSelect, onRemove, getKey = technique => technique.technique }) {
  if (techniques.length === 0) return null
  return (
    <Grid columns={{ count: 2, size: 'auto' }}>
      {techniques.map((technique, index) => (
        <Box direction="row" key={getKey(technique, index)} margin={{ vertical: 'xsmall' }}>
          <CheckBox
            checked={technique.selected}
            label={<Text size="small">{technique.technique}</Text>}
            onChange={() => onSelect(technique.technique)}
          />
          <ActionButton
            label={<Text><FontAwesome icon={['fal', 'minus-circle']} size="xs" /></Text>}
            margin={{ horizontal: 'xsmall' }}
            onClick={() => onRemove(index)}
            plain
            target="_self"
          />
        </Box>
      ))}
    </Grid>
  )
}

export function ServiceDetailsContent ({ tabbedSetupEnabled, serviceHasTerms, setServiceHasTerms, allAcademicYears, serviceDetails, toggleAcademicYear }) {
  return (<Box margin={tabbedSetupEnabled ? { top: 'medium' } : undefined}>
    {tabbedSetupEnabled && <><H3 margin={{ vertical: 'none' }}>
      General Information
    </H3>
    <Divider color="primary" margin={{ vertical: 'small' }} /></>}
    <Box fill direction="row" gap="small">
      <Text margin={{ top: 'xsmall' }} size="large">
        What type of service are you? Eg. School, Children’s Home,
        Health Care Setting.
      </Text>
      <Button
        label={
          <FontAwesome
            color="brand"
            icon={['fas', 'question-circle']}
          />
        }
        plain
        tip={{
          content: (
            <Box
              background={{ color: 'white' }}
              border={{ color: 'brand', size: 'small' }}
              direction="column"
              pad="small"
            >
              <Text>
                What type of service are you? Eg. School, Children’s
                Home, Health Care Setting.
              </Text>
            </Box>
          ),
        }}
      />
    </Box>

    <Text>
      These are the terms that will be used across your the system.
    </Text>

    <Box direction="row-responsive" gap="medium">
      <FormField
        label="Service (singular)"
        name="serviceTerm"
        required
      >
        <TextInput
          name="serviceTerm"
          placeholder="service"
          type="text"
        />
      </FormField>

      <FormField
        label="Service (plural)"
        name="serviceTermPlural"
        required
      >
        <TextInput
          name="serviceTermPlural"
          placeholder="services"
          type="text"
        />
      </FormField>
    </Box>

    <Divider color="primary" margin={{ vertical: 'small' }} />

    <Box fill direction="row" gap="small">
      <Text margin={{ top: 'xsmall' }} size="large">
        How do you refer to the individuals who use your service? Eg.
        Students, Pupils, Young People, Clients.
      </Text>
      <Button
        label={
          <FontAwesome
            color="brand"
            icon={['fas', 'question-circle']}
          />
        }
        plain
        tip={{
          content: (
            <Box
              background={{ color: 'white' }}
              border={{ color: 'brand', size: 'small' }}
              direction="column"
              pad="small"
            >
              <Text>
                How do you refer to the individuals who use your
                service? Eg. Students, Pupils, Young People, Clients.
              </Text>
            </Box>
          ),
        }}
      />
    </Box>

    <Text>
      These are the terms that will be used across your the system.
    </Text>

    <Box direction="column">
      <Box direction="row-responsive" gap="medium">
        <FormField
          label="Service User (singular)"
          name="serviceUserTerm"
          required
        >
          <TextInput
            name="serviceUserTerm"
            placeholder="service user"
            type="text"
          />
        </FormField>

        <FormField
          label="Service Users (plural)"
          name="serviceUserTermPlural"
          required
        >
          <TextInput
            name="serviceUserTermPlural"
            placeholder="service users"
            type="text"
          />
        </FormField>
      </Box>

      {tabbedSetupEnabled && <><Divider color="primary" margin={{ vertical: 'small' }} />
      <Text margin={{ top: 'xsmall', bottom: 'small' }} size="large">
          Record &amp; Plan Names
      </Text>
      <ResponsiveGrid
          columns={{ small: ['auto'], medium: ['auto', 'auto'], large: ['auto', 'auto'], xlarge: ['auto', 'auto'] }}
          gap="small"
        >
        {recordTerms.map(({ key, label }) => (
          <FormField key={key} label={label} name={key} >
            <TextInput name={key} placeholder={label} type="text" />
          </FormField>
        ))}
      </ResponsiveGrid></>}

      <Divider color="primary" margin={{ vertical: 'small' }} />

      <Text margin={{ top: 'xsmall', bottom: 'small' }} size="large">
        Term dates and year groups covered
      </Text>

      <CheckBox
        checked={serviceHasTerms}
        label={<Text>My service has term dates</Text>}
        onChange={() => setServiceHasTerms(!serviceHasTerms)}
      />

      {serviceHasTerms && (
        <>
          <Box direction="row-responsive" gap="medium">
            <FormField
              label="Years Covered"
              name="enabledAcademicYears"
              required
            >

              <ResponsiveContext.Consumer>
                {responsive => {
                  const columnCount = responsive === 'xsmall'
                    ? 2
                    : responsive === 'small'
                      ? 4
                      : responsive === 'medium'
                        ? 4
                        : 8

                  return (
                    <Grid columns={{ count: columnCount, size: 'auto' }} gap="small" width="100%">
                      {allAcademicYears.map(academicYear => (
                        <CheckBox
                          key={academicYear}
                          checked={Array.isArray(serviceDetails.enabledAcademicYears) && serviceDetails.enabledAcademicYears.includes(academicYear)}
                          label={<Text>{academicYear.replaceAll('Year', '')}</Text>}
                          onChange={() => toggleAcademicYear(academicYear)}
                        />
                      ))}
                    </Grid>
                  )
                }}
              </ResponsiveContext.Consumer>
            </FormField>
          </Box>
        </>
      )}
    </Box>
  </Box>)
}

export function TechniquesContent ({ deescalationTechniques, deescalationTechniqueSelect, removeTechnique, deescalationAddButton, addDeescalationTechnique, physicalTechniques, physicalTechniqueSelect, physicalAddButton, addPhysicalTechnique }) {
  return (<Box margin={{ top: 'medium' }}>
    <Divider color="primary" margin={{ vertical: 'small' }} />
    <Box fill direction="row" gap="small">

      <Text margin={{ top: 'xsmall' }} size="large">
        Behaviour Management Techniques
      </Text>
      <Button
        label={
          <FontAwesome
            color="brand"
            icon={['fas', 'question-circle']}
          />
        }
        plain
        tip={{
          content: (
            <Box
              background={{ color: 'white' }}
              border={{ color: 'brand', size: 'small' }}
              direction="column"
              pad="small"
            >
              <Text>
                The techniques entered here will be included in your
                bespoke incident report. These can be changed at any
                time.
              </Text>
            </Box>
          ),
        }}
      />
    </Box>

    <Box
      direction="row-responsive"
      gap="medium"
      pad={{ top: 'small' }}
    >
      <ResponsiveContext.Consumer>
        {responsive => (
          <Box
            direction="column"
            width={['xsmall', 'small'].includes(responsive) ? '100%' : '50%'}
          >
            <Box fill direction="row" gap="small">
              <Text size="small">
                What are the most common De-escalation Techniques used
                in your service ?
              </Text>
              <Button
                alignSelf="start"
                label={
                  <FontAwesome
                    color="brand"
                    icon={['fas', 'question-circle']}
                  />
                }
                plain
                tip={{
                  content: (
                    <Box
                      background={{ color: 'white' }}
                      border={{ color: 'brand', size: 'small' }}
                      direction="column"
                      pad="small"
                    >
                      <Text>
                        Please list the most common de-escalation used
                        within the service setting. EG Empathy
                      </Text>
                      <Text>Verbal advice and support</Text>
                      <Text>Giving space</Text>
                      <Text>Reassurance</Text>
                      <Text>Scripts</Text>
                      <Text>Negotiation</Text>
                      <Text>Positive Choices</Text>
                      <Text>Humour</Text>
                      <Text>Logical Consequences</Text>
                      <Text>Tactical ignoring</Text>
                      <Text>Take up time</Text>
                      <Text>Time-out</Text>
                      <Text>Supportive touch</Text>
                      <Text>Transfer staff</Text>
                      <Text>Success reminded</Text>
                      <Text>Listening</Text>
                      <Text>Acknowledgement</Text>
                      <Text>Removing audience</Text>
                    </Box>
                  ),
                }}
              />
            </Box>
            <TechniqueOptions
              techniques={deescalationTechniques}
              onSelect={deescalationTechniqueSelect}
              onRemove={index => removeTechnique('deescalation', index)}
            />
            <Box direction="row" gap="small">
              <FormField name="deescalationTechnique">
                <TextInput
                  name="deescalationTechnique"
                  placeholder="new technique"
                  type="text"
                />
              </FormField>

              <ActionButton
                label={
                  <Text>
                    <FontAwesome icon={['fal', 'plus-circle']} /> Add
                  </Text>
                }
                disabled={!deescalationAddButton}
                color="primary"
                onClick={() => addDeescalationTechnique()}
                style={{ height: '35px' }}
              />
            </Box>
          </Box>
        )}
      </ResponsiveContext.Consumer>

      <ResponsiveContext.Consumer>
        {responsive => (
          <Box
            direction="column"
            width={['xsmall', 'small'].includes(responsive) ? '100%' : '50%'}
          >
            <Box fill direction="row" gap="small">
              <Text size="small">
                Name the Physical Interventions used in your service
                (If applicable)
              </Text>
              <Button
                alignSelf="start"
                label={
                  <FontAwesome
                    color="brand"
                    icon={['fas', 'question-circle']}
                  />
                }
                plain
                tip={{
                  content: (
                    <Box
                      background={{ color: 'white' }}
                      border={{ color: 'brand', size: 'small' }}
                      direction="column"
                      pad="small"
                    >
                      <Text>
                        Please list the Physical techniques used
                        within the service setting.
                      </Text>
                      <Text>Figure of 4</Text>
                      <Text>Single elbow</Text>
                      <Text>Break way techniques</Text>
                      <Text>Team Control</Text>
                      <Text>Guiding hold</Text>
                      <Text>Double Embrace</Text>
                      <Text>
                        The name of techniques used and trained within
                        your service will be available from you
                        training provider or course workbook.
                      </Text>
                    </Box>
                  ),
                }}
              />
            </Box>
            <TechniqueOptions
              techniques={physicalTechniques}
              onSelect={physicalTechniqueSelect}
              onRemove={index => removeTechnique('physical', index)}
              getKey={(_technique, index) => index}
            />
            <Box direction="row" gap="small">
              <FormField name="physicalTechnique">
                <TextInput
                  name="physicalTechnique"
                  placeholder="new technique"
                  type="text"
                />
              </FormField>

              <ActionButton
                label={
                  <Text>
                    <FontAwesome icon={['fal', 'plus-circle']} /> Add
                  </Text>
                }
                disabled={!physicalAddButton}
                onClick={() => addPhysicalTechnique()}
                color="primary"
                style={{ height: '35px' }}
              />
            </Box>
          </Box>
        )}
      </ResponsiveContext.Consumer>
    </Box>
  </Box>)
}

export function ClickersContent ({ tabbedSetupEnabled, clickerColumns, quickBehaviours, addQuickBehaviourRow, clickerGroups, clickerGroupColumns, toggleLegacyQuickBehaviour, removeLegacyQuickBehaviour, serviceDetails, addLegacyQuickBehaviour }) {
  return (<Box margin={{ top: 'medium' }}>
    <Divider color="primary" margin={{ top: 'large', vertical: 'medium' }} />

    <Box fill direction="row" gap="small">
      <Text margin={{ top: 'xsmall' }} size="large">
        Behaviour Clicker
      </Text>
      {/* <ActionButton
                  label={
                    <FontAwesome
                      color="brand"
                      icon={['fas', 'question-circle']}
                    />
                  }
                  plain
                  tip={{
                    content: (
                      <Box
                        background={{ color: 'white' }}
                        border={{ color: 'brand', size: 'small' }}
                        direction="column"
                        pad="small"
                      >
                        <Text>
                          The techniques entered here will be included in your
                          bespoke incident report. These can be changed at any
                          time.
                        </Text>
                      </Box>
                    ),
                  }}
                /> */}
    </Box>

    <Box flex={false} pad={{ top: 'small' }}>
      <Text size="small">
        The Behaviour Clicker can be used for both low level repetitive and positive behaviours
      </Text>
    </Box>
    {tabbedSetupEnabled ? <>
    <Box flex={false} margin={{ top: 'small' }}>
      <DataTable
        columns={clickerColumns}
        data={quickBehaviours}
        primaryKey="rowKey"
        border={{ color: '#ddd' }}
      />
    </Box>
    <Box flex={false} direction="row" justify="start" margin={{ top: 'small' }}>
      <ActionButton
        label={<Text><FontAwesome icon={['fal', 'plus-circle']} />&nbsp;Add Row</Text>}
        margin={{ horizontal: 'xsmall' }}
        style={{ background: 'besmartOrange' }}
        onClick={() => addQuickBehaviourRow()}
        width="small"
        target='_self'
      />
    </Box>

    {clickerGroups.length > 0 && <>
      <Box flex={false} margin={{ top: 'medium' }}>
        <Text size="small">
          Group Colours
        </Text>
      </Box>
      <Box flex={false} margin={{ top: 'small' }}>
        <DataTable
          columns={clickerGroupColumns}
          data={clickerGroups.map(group => ({ group }))}
          primaryKey="group"
          border={{ color: '#ddd' }}
        />
      </Box>
    </>}
    </> : <>
      <Grid columns={{ count: 2, size: 'auto' }}>
        {quickBehaviours.map((behaviour, index) => (
          <Box direction="row" key={behaviour.quickBehaviour} margin={{ vertical: 'xsmall' }}>
            <CheckBox
              checked={behaviour.selected}
              label={<Text size="small">{behaviour.quickBehaviour}</Text>}
              onChange={() => toggleLegacyQuickBehaviour(behaviour.quickBehaviour)}
            />
            <ActionButton
              label={<Text><FontAwesome icon={['fal', 'minus-circle']} size="xs" /></Text>}
              margin={{ horizontal: 'xsmall' }}
              onClick={() => removeLegacyQuickBehaviour(index)}
              plain
              target="_self"
            />
          </Box>
        ))}
      </Grid>
      <Box direction="row" gap="small">
        <FormField name="quickBehaviour">
          <TextInput name="quickBehaviour" placeholder="New Behaviour" type="text" />
        </FormField>
        <ActionButton
          label={<Text><FontAwesome icon={['fal', 'plus-circle']} /> Add</Text>}
          disabled={!serviceDetails.quickBehaviour}
          color="primary"
          onClick={addLegacyQuickBehaviour}
          style={{ height: '35px' }}
        />
      </Box>
    </>}
  </Box>
  )
}

export function PermissionsTabContent ({ isLoading, setSafeguardingLeads, allStaff, safeguardingLeads, setOtherSystemAdmins, setOtherSystemAdminsHasChanged, otherSystemAdmins }) {
  return (<Box margin={{ top: 'medium' }}>
    <H3 margin={{ vertical: 'none' }}>
      System Permissions
    </H3>
    <Divider color="primary" margin={{ vertical: 'small' }} />


    <Box
      fill
      direction="column"
      gap="small"
      margin={{ top: 'none', bottom: 'small' }}
    >
      <Text margin={{ top: 'xsmall' }} size="large">
        Analytics, System Users, and all Incidents
      </Text>

      <Text size="small">
        Staff entered below will have access to the System Users Page, Analytics Page and all incident reports.
      </Text>
      <Box direction="row" gap="medium" width="100%">

        <SelectDynamic
          isMulti
          isClearable={false}
          isLoading={isLoading}
          width="100%"
          onChange={(selectedOptions) => {
            setSafeguardingLeads(selectedOptions)
          }}
          options={allStaff?.filter(staff => !safeguardingLeads.map(lead => lead.email).includes(staff.email)).map(staff => ({
            label: `${staff.firstName} ${staff.lastName}`,
            value: staff.id,
            firstName: staff.firstName,
            lastName: staff.lastName,
            email: staff.email
          }))}
          value={safeguardingLeads.map(lead => ({
            label: `${lead.firstName} ${lead.lastName}`,
            value: lead.email,
            firstName: lead.firstName,
            lastName: lead.lastName,
            email: lead.email
          }))}
          ma
        />
      </Box>
    </Box>

    <Divider color="primary" margin={{ vertical: 'small' }} />


    <Box
      fill
      direction="column"
      gap="small"
      margin={{ top: 'none', bottom: 'small' }}
    >
      <Text margin={{ top: 'xsmall' }} size="large">
        System Admins
      </Text>
      <Text size="small">
        Staff entered below will have access to Service Setup (this page). The organisation admin will always have access to this page, even if they&lsquo;re not listed below.
      </Text>
      <Box direction="row" gap="medium" width="100%">

        <SelectDynamic
          isMulti
          isClearable={false}
          isLoading={isLoading}
          width="100%"
          onChange={(selectedOptions) => {
            setOtherSystemAdmins(selectedOptions)
            setOtherSystemAdminsHasChanged(true)
          }}
          options={allStaff?.filter(staff => !otherSystemAdmins.map(admin => admin.email).includes(staff.email)).map(staff => ({
            label: `${staff.firstName} ${staff.lastName}`,
            value: staff.email,
            id: staff.id,
            firstName: staff.firstName,
            lastName: staff.lastName,
            email: staff.email
          }))}
          value={otherSystemAdmins.map(admin => ({
            label: `${admin.firstName} ${admin.lastName}`,
            value: admin.email,
            id: admin.id,
            firstName: admin.firstName,
            lastName: admin.lastName,
            email: admin.email
          }))}
          ma
        />
      </Box>
    </Box>
  </Box>)
}

export function RecipientsContent ({ tabbedSetupEnabled, behaviourLeadColumns, mappedBehaviourLeads, addBehaviourLead }) {
  return (<Box margin={{ top: 'medium' }}>
    {tabbedSetupEnabled && <><H3 margin={{ vertical: 'none' }}>
      Notifications
    </H3>
    <Divider color="primary" margin={{ vertical: 'small' }} /></>}

    <Box fill gap="none" margin={{ bottom: 'small' }}>
      <Text weight="bold" size="large">
        Receive all incidents
      </Text>
      <Text margin={{ top: 'xsmall', bottom: 'none' }} size="large">
        System Users who&lsquo;s email addresses are added below will receive a copy of all Incidents created in the system.
      </Text>
    </Box>

    <Box direction="row-responsive" gap="medium">
      <ResponsiveContext.Consumer>
        {responsive => (
          <DataTable
            columns={
              ['xsmall', 'small'].includes(responsive)
                ? behaviourLeadColumns?.filter(
                  el =>
                    el.property !== 'phone' &&
                    el.property !== 'email',
                )
                : behaviourLeadColumns
            }
            data={mappedBehaviourLeads}
            fill="horizontal"
            size="large"
          />
        )}
      </ResponsiveContext.Consumer>
    </Box>
    <Box direction="row-responsive" gap="medium">
      <FormField label="First Name" name="firstName">
        <TextInput name="firstName" type="text" />
      </FormField>

      <FormField label="Last Name" name="lastName">
        <TextInput name="lastName" type="text" />
      </FormField>

      <FormField label="Phone Number" name="phone">
        <TextInput name="phone" type="text" />
      </FormField>

      <FormField label="Email" name="email">
        <TextInput name="email" type="email" />
      </FormField>

      {/* <ActionButton disabled={!behaviourLeadAddButton} label={<Text><FontAwesomeIcon icon={['fal', 'plus-circle']} /></Text>} color='primary' onClick={() => addLead()} style={{ height: '35px' }} alignSelf='end' margin={{ bottom: 'small' }} /> */}
      <ActionButton
        label={<Text>Add</Text>}
        color="primary"
        onClick={() => addBehaviourLead()}
        style={{ height: '35px' }}
        alignSelf="end"
        margin={{ bottom: 'small' }}
      />
    </Box>
  </Box>)
}

export function NotificationRulesContent ({ resultColumns, notificationRules, addRuleRow }) {
  return (<Box margin={{ top: 'medium' }}>
    <Divider color="primary" margin={{ vertical: 'small' }} />

    <Box fill gap="none" margin={{ bottom: 'small' }}>
      <Text weight="bold" size="large">
        Granular Incident Notification Settings
      </Text>
      <Text margin={{ top: 'xsmall', bottom: 'none' }} size="large">
        Users below will receive notifications when an incident occurs for any of the filters below.
      </Text>
    </Box>

    <Box>
      <DataTable
        columns={resultColumns}
        data={notificationRules}
        primaryKey="rowKey"
        border={{ color: '#ddd' }}
        step={250}
        paginate={true}
      // sort={sort}
      // onSort={setSort}
      />

      <Box direction="row" justify="start" margin={{ top: 'small' }}>
        <ActionButton
          label={<Text><FontAwesome icon={['fal', 'plus-circle']} />&nbsp;Add Row</Text>}
          margin={{ horizontal: 'xsmall' }}
          style={{ background: 'besmartOrange' }}
          onClick={() => addRuleRow()}
          width="small"
          target='_self'
        />
      </Box>

    </Box>
  </Box>)
}

export function PILTabContent ({ enablePILExtras, setEnablePILExtras }) {
  return (<Box margin={{ top: 'medium' }}>
    <H3 margin={{ vertical: 'none' }}>
      Prejudice-related incidents settings.
    </H3>

    <Divider color="primary" margin={{ vertical: 'small' }} />
    <CheckBox
      checked={enablePILExtras}
      label={
        <Text>
          <Text weight='bold'>Record prejudice-related incidents</Text>&nbsp;
          Turn this on to log and analyse bullying and incidents of prejudiced or discriminatory behaviour, such as racist, sexist, disability-related, homophobic, biphobic and transphobic incidents. This includes any suspensions or permanent exclusions that follow.
        </Text>
      }
      onChange={() => setEnablePILExtras(!enablePILExtras)}
    />

    <Divider color="primary" margin={{ vertical: 'small' }} />

    {enablePILExtras && <>
      <Box fill direction="row" gap="small" margin={{ top: 'small' }}>
        <Text margin={{ top: 'xsmall' }} size="large">
          Prejudice-related incident terminology
        </Text>
        <Button
          label={
            <FontAwesome
              color="brand"
              icon={['fas', 'question-circle']}
            />
          }
          plain
          tip={{
            content: (
              <Box
                background={{ color: 'white' }}
                border={{ color: 'brand', size: 'small' }}
                direction="column"
                pad="small"
              >
                <Text>
                  Use the boxes below to customise the labels for your school
                </Text>
              </Box>
            ),
          }}
        />
      </Box>

      <Text>
        Staff see these names when they log an incident, and they appear in your reports. Edit any name to match
        your behaviour, anti-bullying or equality policy.
      </Text>
      <Box direction="column" margin={{ top: 'medium' }}>
        <ResponsiveGrid
          rows={['auto']}
          columns={{
            small: ['auto'],
            medium: ['auto', 'auto'],
            large: ['auto', 'auto', 'auto'],
            xlarge: ['auto', 'auto', 'auto'],
          }}
          gap="small"
        >
          {constants.pilKeys.map((pil) => (
            <FormField
              key={TermsService.getPILTermKeyToTagKey(pil.key)}
              label={pil.label}
              name={TermsService.getPILTermKeyToTagKey(pil.key)}

            >
              <TextInput
                name={TermsService.getPILTermKeyToTagKey(pil.key)}
                placeholder={pil.label}
                type="text"
              />
            </FormField>
          ))}
        </ResponsiveGrid>
      </Box>
    </>}
  </Box>
  )
}

export function AIContent ({ tabbedSetupEnabled, aiTermsAccepted, setAiTermsAccepted }) {
  return (<Box margin={{ top: 'medium' }}>
    {tabbedSetupEnabled && <><H3 margin={{ vertical: 'none' }}>
      Integration Settings
    </H3>
    <Divider color="primary" margin={{ vertical: 'small' }} /></>}
    <Box fill gap="small">
      <Text margin={{ top: 'xsmall' }} size="large"> BehaviourSmart AI</Text>



      <CheckBox
        checked={aiTermsAccepted}
        label={
          <Text>
            Our AI suggestions are for informational purposes only. Users should evaluate and apply these ideas based on their expertise. Behaviour Smart is not liable for outcomes from these suggestions. Always consult a qualified professional for personalised guidance. Please do not share data with parents or other parties that you&lsquo;re not authorised to. For more information please visit <Anchor href="https://www.behavioursmart.co.uk/aipowered" target="_blank">https://www.behavioursmart.co.uk/aipowered</Anchor>
          </Text>
        }
        onChange={() => setAiTermsAccepted(!aiTermsAccepted)}
      />
    </Box>
  </Box>)
}

export function WondeContent ({ wondeIntegration, setWondeIntegration, tabbedSetupEnabled = true }) {
  return (<Box
    margin={tabbedSetupEnabled ? { top: 'medium' } : { bottom: 'medium' }}
    background={tabbedSetupEnabled ? undefined : 'white'}
    round={tabbedSetupEnabled ? undefined : 'small'}
  >
    {tabbedSetupEnabled && <Divider color="primary" margin={{ vertical: 'medium' }} />}
    <Box
      fill={tabbedSetupEnabled ? true : undefined}
      gap="small"
      margin={tabbedSetupEnabled ? undefined : { horizontal: 'small' }}
      pad={tabbedSetupEnabled ? undefined : 'small'}
    >
      <Text margin={{ top: 'xsmall' }} size="large">Wonde Integration</Text>
      <CheckBox
        checked={wondeIntegration}
        label={<Text>Enable MIS Integration</Text>}
        onChange={() => setWondeIntegration(!wondeIntegration)}
      />
    </Box>
  </Box>)
}

export function CPOMSContent ({ cpomsDomain, cpomsEnabled, setCpomsEnabled, cpomsSyncEverything, setCpomsSyncEverything, tabbedSetupEnabled = true }) {
  return cpomsDomain && <>
    {tabbedSetupEnabled && <Divider color="primary" margin={{ vertical: 'medium' }} />}

    <Box background='white' direction='column' gap='small' round='small' flex='grow' margin={{ bottom: 'medium' }}>
      <Box
        fill={tabbedSetupEnabled ? true : undefined}
        gap="small"
        margin={tabbedSetupEnabled ? undefined : { horizontal: 'small' }}
        pad={tabbedSetupEnabled ? undefined : { top: 'small', horizontal: 'small', bottom: 'medium' }}
      >
        <Text margin={{ top: 'xsmall' }} size='large'>CPOMS Integration</Text>
        <CheckBox checked={cpomsEnabled} label={<Text>Enable CPOMS Integration</Text>} onChange={() => setCpomsEnabled(!cpomsEnabled)} />
        {cpomsEnabled && <CheckBox checked={cpomsSyncEverything} label={<Text>Sync all incidents with CPOMS (leave unchecked to require incidents to be manually selected)</Text>} onChange={() => setCpomsSyncEverything(!cpomsSyncEverything)} />}

      </Box>
    </Box>
  </>
}
